import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Check,
  CheckCircle2,
  Eye,
  EyeOff,
  ExternalLink,
  FileText,
  KeyRound,
  LockKeyhole,
  Mail,
  Phone,
  Save,
  Send,
  ShieldCheck,
  UserRound,
  ClipboardList,
} from "lucide-react";

import { useAppDispatch, useAppSelector } from "../hooks/reducer";
import {
  changePassword,
  createTelegramLink,
  updateUserData,
  uploadResume,
} from "../store/slices/auth";
import MaterialDropzone from "../components/NewView/Common/MaterialDropzone";
import ProgramMaterials from "../components/NewView/Common/ProgramMaterials";
import { CandidateForm, ProgramMaterial, programApi } from "../api/program";

type Notification = {
  type: "success" | "error" | null;
  message: string;
};

const Settings = () => {
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.authReducer.data?.user);

  const [formData, setFormData] = useState({
    fullName: "",
    phoneNumber: "",
    emailAddress: "",
    bio: "",
  });
  const [applicationForm, setApplicationForm] = useState<CandidateForm>({
    city: "",
    participantStatus: "",
    age: undefined,
    education: "",
    employmentStatus: "",
    currentJob: "",
    previousJob: "",
    hasChildren: false,
    motivation: "",
    threeMonthGoal: "",
    communityContribution: "",
  });
  const [applicationStatus, setApplicationStatus] = useState("");
  const [applicationLoading, setApplicationLoading] = useState(false);
  const [notification, setNotification] = useState<Notification>({
    type: null,
    message: "",
  });
  const [saving, setSaving] = useState(false);
  const [resumeFiles, setResumeFiles] = useState<File[]>([]);
  const [resumeLoading, setResumeLoading] = useState(false);
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [showPasswords, setShowPasswords] = useState(false);
  const [passwordData, setPasswordData] = useState({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    if (!user) return;

    const publicPhone = user.telephone?.startsWith("google:")
      ? ""
      : user.telephone || "";

    setFormData({
      fullName: user.username || "",
      phoneNumber: publicPhone,
      emailAddress: user.email || "",
      bio: user.bio || "",
    });
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const loadApplication = async () => {
      try {
        const data = await programApi.getMyApplication();
        setApplicationStatus(data.application.status);
        setApplicationForm({
          city: data.application.form.city || "",
          participantStatus: data.application.form.participantStatus || "",
          age: data.application.form.age || undefined,
          education: data.application.form.education || "",
          employmentStatus: data.application.form.employmentStatus || "",
          currentJob: data.application.form.currentJob || "",
          previousJob: data.application.form.previousJob || "",
          hasChildren: Boolean(data.application.form.hasChildren),
          motivation: data.application.form.motivation || "",
          threeMonthGoal: data.application.form.threeMonthGoal || "",
          communityContribution:
            data.application.form.communityContribution || "",
        });
      } catch (error) {
        console.error("Load application error:", error);
      }
    };

    loadApplication();
  }, [user]);

  useEffect(() => {
    if (!notification.type) return;

    const timeout = window.setTimeout(
      () => setNotification({ type: null, message: "" }),
      5000
    );

    return () => window.clearTimeout(timeout);
  }, [notification.type]);

  const isTelegramLinked = Boolean(user?.telegram?.isVerified);
  const isEmailMissing = !formData.emailAddress.trim();
  const role =
    user?.role === "super_admin"
      ? "Супер-адмін"
      : user?.role === "career_manager"
      ? "Кар'єрний менеджер"
      : user?.role === "education_admin" || user?.role === "admin" || user?.isAdmin
      ? "Освітній адмін"
      : user?.programAccess === "active"
      ? "Учасник"
      : "Кандидат";
  const displayName = user?.username || "Користувач Yellow Hub";
  const initials = displayName
    .split(" ")
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const profileCompletion = useMemo(() => {
    const values = [
      formData.fullName,
      formData.phoneNumber,
      formData.emailAddress,
      formData.bio,
      user?.resume?.url || "",
    ];
    const completed = values.filter((value) => value.trim()).length;
    return Math.round((completed / values.length) * 100);
  }, [formData, user?.resume?.url]);

  const resumeMaterial = useMemo<ProgramMaterial[]>(() => {
    if (!user?.resume?.url) return [];

    return [
      {
        _id: "profile-resume",
        kind: "file",
        title: user.resume.name || "Резюме",
        name: user.resume.name || "Резюме",
        url: user.resume.url,
        mimeType: user.resume.mimeType,
        size: user.resume.size,
        dropboxPath: user.resume.dropboxPath,
        uploadedAt: user.resume.uploadedAt,
      },
    ];
  }, [user?.resume]);

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData((previous) => ({ ...previous, [name]: value }));
  };

  const handleApplicationChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = event.target;
    setApplicationForm((previous) => ({
      ...previous,
      [name]:
        name === "age"
          ? value
            ? Number(value)
            : undefined
          : name === "hasChildren"
          ? value === "true"
          : value,
    }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNotification({ type: null, message: "" });

    if (!formData.fullName.trim() || !formData.phoneNumber.trim()) {
      setNotification({
        type: "error",
        message: "Заповніть ім'я та номер телефону.",
      });
      return;
    }

    try {
      setSaving(true);
      await dispatch(
        updateUserData({
          username: formData.fullName.trim(),
          telephone: formData.phoneNumber.trim(),
          email: formData.emailAddress.trim(),
          bio: formData.bio.trim(),
        }) as any
      ).unwrap();

      setNotification({
        type: "success",
        message: "Зміни профілю збережено.",
      });
    } catch (error: any) {
      setNotification({
        type: "error",
        message: String(error?.message || error || "Не вдалося оновити профіль."),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleApplicationSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setNotification({ type: null, message: "" });

    try {
      setApplicationLoading(true);
      const application = await programApi.updateMyApplication(applicationForm);
      setApplicationStatus(application.status);
      setNotification({
        type: "success",
        message: "Анкету кандидата збережено.",
      });
    } catch (error: any) {
      setNotification({
        type: "error",
        message:
          error.response?.data?.message ||
          "Не вдалося зберегти анкету кандидата.",
      });
    } finally {
      setApplicationLoading(false);
    }
  };

  const handleResumeUpload = async () => {
    const file = resumeFiles[0];
    setNotification({ type: null, message: "" });

    if (!file) {
      setNotification({
        type: "error",
        message: "Додайте файл резюме перед завантаженням.",
      });
      return;
    }

    const extension = file.name.split(".").pop()?.toLowerCase();
    if (!extension || !["pdf", "doc", "docx", "txt"].includes(extension)) {
      setNotification({
        type: "error",
        message: "Резюме можна завантажити у форматі PDF, DOC, DOCX або TXT.",
      });
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setNotification({
        type: "error",
        message: "Файл резюме має бути не більшим за 10 MB.",
      });
      return;
    }

    try {
      setResumeLoading(true);
      await dispatch(uploadResume(file) as any).unwrap();
      setResumeFiles([]);
      setNotification({
        type: "success",
        message: "Резюме завантажено.",
      });
    } catch (error: any) {
      setNotification({
        type: "error",
        message: String(error || "Не вдалося завантажити резюме."),
      });
    } finally {
      setResumeLoading(false);
    }
  };

  const handleTelegramLink = async () => {
    try {
      setTelegramLoading(true);
      const result = await dispatch(createTelegramLink() as any).unwrap();

      if (result?.telegramUrl) {
        window.open(result.telegramUrl, "_blank", "noopener,noreferrer");
        setNotification({
          type: "success",
          message: "Telegram відкрито. Натисніть Start у боті Yellow Hub.",
        });
      }
    } catch (error: any) {
      setNotification({
        type: "error",
        message: String(error || "Не вдалося створити посилання Telegram."),
      });
    } finally {
      setTelegramLoading(false);
    }
  };

  const handlePasswordChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = event.target;
    setPasswordData((previous) => ({ ...previous, [name]: value }));
  };

  const handlePasswordSubmit = async (
    event: React.FormEvent<HTMLFormElement>
  ) => {
    event.preventDefault();
    setNotification({ type: null, message: "" });

    if (
      !passwordData.oldPassword ||
      !passwordData.newPassword ||
      !passwordData.confirmPassword
    ) {
      setNotification({
        type: "error",
        message: "Заповніть усі поля для зміни пароля.",
      });
      return;
    }

    if (!/^(?=.*[A-Z]).{8,}$/.test(passwordData.newPassword)) {
      setNotification({
        type: "error",
        message:
          "Новий пароль має містити щонайменше 8 символів і одну велику літеру.",
      });
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setNotification({
        type: "error",
        message: "Новий пароль і підтвердження не збігаються.",
      });
      return;
    }

    if (passwordData.oldPassword === passwordData.newPassword) {
      setNotification({
        type: "error",
        message: "Новий пароль має відрізнятися від поточного.",
      });
      return;
    }

    try {
      setPasswordLoading(true);
      await dispatch(
        changePassword({
          oldPassword: passwordData.oldPassword,
          newPassword: passwordData.newPassword,
        }) as any
      ).unwrap();

      setPasswordData({
        oldPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setShowPasswords(false);
      setNotification({
        type: "success",
        message: "Пароль успішно змінено.",
      });
    } catch (error: any) {
      const message =
        String(error) === "Old password is incorrect"
          ? "Поточний пароль введено неправильно."
          : String(error || "Не вдалося змінити пароль.");

      setNotification({ type: "error", message });
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <section className="min-h-full bg-[#f4f7fb] px-4 pb-6 pt-6 sm:px-6 lg:px-8 lg:pt-8">
      <div className="mx-auto max-w-[1320px]">
        {notification.type && (
          <div
            role="status"
            className={`mb-5 flex items-center gap-3 rounded-lg border px-4 py-3 text-sm font-medium ${
              notification.type === "success"
                ? "border-[#B9D8C1] bg-[#F0F8F2] text-[#276537]"
                : "border-[#E9BDB5] bg-[#FFF3F1] text-[#9D3D2E]"
            }`}
          >
            {notification.type === "success" ? (
              <CheckCircle2 size={19} />
            ) : (
              <AlertCircle size={19} />
            )}
            {notification.message}
          </div>
        )}

        <div className="mb-6 border-b border-[#E0DCD5] pb-5">
          <p className="text-sm font-black uppercase tracking-[0.14em] text-[#8a6f00]">Обліковий запис</p>
          <div className="mt-1 flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <h2 className="text-2xl font-bold text-[#29251F] sm:text-3xl">
                Керуйте особистими даними
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-[#777068]">
                Актуальні контакти допомагають підтримувати доступ до курсів і
                безпечно відновлювати пароль.
              </p>
            </div>
            <div className="flex items-center gap-2 text-sm text-[#615B54]">
              <ShieldCheck size={18} className="text-[#8a6f00]" />
              Захист акаунта активний
            </div>
          </div>
        </div>

        <div className="grid gap-6 xl:grid-cols-[300px_minmax(0,1fr)]">
          <aside className="space-y-5">
            <div className="rounded-lg border border-[#E3DED6] bg-white p-5">
              <div className="flex items-center gap-4">
                <div className="grid h-16 w-16 shrink-0 place-items-center rounded-lg bg-[#F2B544] text-xl font-bold text-[#3F2B08]">
                  {initials}
                </div>
                <div className="min-w-0">
                  <h3 className="truncate text-lg font-bold text-[#312D28]">
                    {displayName}
                  </h3>
                  <p className="mt-1 text-sm text-[#857E76]">{role}</p>
                </div>
              </div>

              <div className="mt-6">
                <div className="flex items-center justify-between text-sm">
                  <span className="font-medium text-[#5B554E]">Профіль заповнено</span>
                  <span className="font-bold text-[#B64B1F]">{profileCompletion}%</span>
                </div>
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-[#EEEAE4]">
                  <div
                    className="h-full rounded-full bg-[#ffd400] transition-all"
                    style={{ width: `${profileCompletion}%` }}
                  />
                </div>
              </div>

              <dl className="mt-6 space-y-3 border-t border-[#EEEAE4] pt-5 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-[#857E76]">Телефон</dt>
                  <dd className="truncate font-medium text-[#433E38]">
                    {user?.telephone || "Не вказано"}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-[#857E76]">Telegram</dt>
                  <dd
                    className={`inline-flex items-center gap-1.5 font-medium ${
                      isTelegramLinked ? "text-[#28713C]" : "text-[#A86025]"
                    }`}
                  >
                    <span
                      className={`h-2 w-2 rounded-full ${
                        isTelegramLinked ? "bg-[#42A65A]" : "bg-[#EAA044]"
                      }`}
                    />
                    {isTelegramLinked ? "Підключено" : "Не підключено"}
                  </dd>
                </div>
              </dl>
            </div>

            <div className="rounded-lg border border-[#D9E2EA] bg-[#F4F8FB] p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="grid h-10 w-10 place-items-center rounded-lg bg-[#2AABEE] text-white">
                  <Send size={19} />
                </div>
                {isTelegramLinked && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[#E2F4E7] px-2.5 py-1 text-xs font-semibold text-[#28713C]">
                    <Check size={13} />
                    Підключено
                  </span>
                )}
              </div>

              <h3 className="mt-4 text-lg font-bold text-[#2D353C]">
                Відновлення через Telegram
              </h3>
              <p className="mt-2 text-sm leading-6 text-[#68747D]">
                Отримуйте одноразовий код у боті Yellow Hub, якщо забудете пароль.
              </p>

              {isTelegramLinked && user?.telegram?.username && (
                <p className="mt-3 text-sm font-semibold text-[#277BA8]">
                  @{user.telegram.username}
                </p>
              )}

              <button
                type="button"
                onClick={handleTelegramLink}
                disabled={telegramLoading}
                className="mt-5 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-[#2AABEE] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#2298D5] disabled:cursor-wait disabled:opacity-60"
              >
                {telegramLoading
                  ? "Створюємо посилання..."
                  : isTelegramLinked
                  ? "Підключити інший акаунт"
                  : "Підключити Telegram"}
                {!telegramLoading && <ExternalLink size={16} />}
              </button>
            </div>
          </aside>

          <div className="space-y-6">
            <form
              onSubmit={handleSubmit}
              className="rounded-lg border border-[#E3DED6] bg-white"
            >
              <div className="border-b border-[#EEEAE4] px-5 py-5 sm:px-7">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-lg bg-[#FFF0E8] text-[#E25720]">
                    <UserRound size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#312D28]">
                      Особиста інформація
                    </h3>
                    <p className="text-sm text-[#857E76]">
                      Дані, які використовуються у вашому профілі.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6 px-5 py-6 sm:px-7">
                <div className="grid gap-5 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#4E4943]">
                      <UserRound size={16} className="text-[#A66B4F]" />
                      Ім'я та прізвище
                    </span>
                    <input
                      type="text"
                      name="fullName"
                      autoComplete="name"
                      className="h-12 w-full rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-semibold text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                      value={formData.fullName}
                      onChange={handleChange}
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#4E4943]">
                      <Phone size={16} className="text-[#A66B4F]" />
                      Номер телефону
                    </span>
                    <input
                      type="tel"
                      name="phoneNumber"
                      autoComplete="tel"
                      className="h-12 w-full rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-semibold text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#4E4943]">
                    <Mail size={16} className="text-[#A66B4F]" />
                    Електронна адреса
                  </span>
                  <input
                    type="email"
                    name="emailAddress"
                    autoComplete="email"
                    className={`h-12 w-full rounded-lg border bg-[#FCFBF9] px-4 text-sm text-[#39342F] outline-none transition focus:bg-white focus:ring-2 ${
                      isEmailMissing
                        ? "border-[#DF9A8E] focus:border-[#C95946] focus:ring-[#C95946]/15"
                        : "border-black/10 focus:border-black focus:ring-[#ffd400]/30"
                    }`}
                    value={formData.emailAddress}
                    onChange={handleChange}
                  />
                  {isEmailMissing && (
                    <span className="mt-2 block text-xs font-medium text-[#A84637]">
                      Додайте email як додатковий контакт.
                    </span>
                  )}
                </label>

                <label className="block">
                  <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#4E4943]">
                    <FileText size={16} className="text-[#A66B4F]" />
                    Про себе
                  </span>
                  <textarea
                    name="bio"
                    rows={6}
                    maxLength={500}
                    placeholder="Коротко розкажіть про себе, свої інтереси або навчальні цілі."
                    className="w-full resize-y rounded-2xl border-2 border-black/10 bg-white px-4 py-3 text-sm font-semibold leading-6 text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                    value={formData.bio}
                    onChange={handleChange}
                  />
                  <span className="mt-2 block text-right text-xs text-[#989087]">
                    {formData.bio.length}/500
                  </span>
                </label>
              </div>

              <div className="flex flex-col-reverse gap-3 border-t border-[#EEEAE4] bg-[#FCFBF9] px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7">
                <p className="text-xs leading-5 text-[#857E76]">
                  Зміни будуть застосовані до всього вашого профілю Yellow Hub.
                </p>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000] transition hover:-translate-y-0.5 disabled:cursor-wait disabled:opacity-60"
                >
                  <Save size={17} />
                  {saving ? "Зберігаємо..." : "Зберегти зміни"}
                </button>
              </div>
            </form>

            <form
              onSubmit={handleApplicationSubmit}
              className="rounded-lg border border-[#E3DED6] bg-white"
            >
              <div className="border-b border-[#EEEAE4] px-5 py-5 sm:px-7">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-lg bg-[#FFF7C8] text-black">
                    <ClipboardList size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#312D28]">
                      Анкета кандидата МОЖУ
                    </h3>
                    <p className="text-sm text-[#857E76]">
                      Ці відповіді допомагають команді підібрати потік,
                      консультації та подальший маршрут.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-6 px-5 py-6 sm:px-7">
                {applicationStatus && (
                  <div className="rounded-2xl border border-[#E8E1D8] bg-[#fffdf7] px-4 py-3 text-sm font-bold text-[#5B554E]">
                    Статус заявки:{" "}
                    <span className="text-black">{applicationStatus}</span>
                  </div>
                )}

                <div className="grid gap-5 md:grid-cols-2">
                  <label className="block">
                    <span className="text-sm font-black uppercase text-black">
                      Місто / район
                    </span>
                    <input
                      name="city"
                      value={applicationForm.city}
                      onChange={handleApplicationChange}
                      className="mt-2 h-12 w-full rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-semibold text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-black uppercase text-black">
                      Статус
                    </span>
                    <select
                      name="participantStatus"
                      value={applicationForm.participantStatus}
                      onChange={handleApplicationChange}
                      className="mt-2 h-12 w-full rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-semibold text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                    >
                      <option value="">Оберіть статус</option>
                      <option value="internally_displaced">ВПО</option>
                      <option value="local">Місцевий житель</option>
                      <option value="veteran">Ветеран</option>
                      <option value="military_family">
                        Член родини військового
                      </option>
                      <option value="other">Інше</option>
                    </select>
                  </label>

                  <label className="block">
                    <span className="text-sm font-black uppercase text-black">
                      Вік
                    </span>
                    <input
                      type="number"
                      min={14}
                      max={120}
                      name="age"
                      value={applicationForm.age || ""}
                      onChange={handleApplicationChange}
                      className="mt-2 h-12 w-full rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-semibold text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-black uppercase text-black">
                      Освіта
                    </span>
                    <input
                      name="education"
                      value={applicationForm.education}
                      onChange={handleApplicationChange}
                      className="mt-2 h-12 w-full rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-semibold text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-black uppercase text-black">
                      Поточна зайнятість
                    </span>
                    <input
                      name="employmentStatus"
                      value={applicationForm.employmentStatus}
                      onChange={handleApplicationChange}
                      className="mt-2 h-12 w-full rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-semibold text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-black uppercase text-black">
                      Чи є діти?
                    </span>
                    <select
                      name="hasChildren"
                      value={applicationForm.hasChildren ? "true" : "false"}
                      onChange={handleApplicationChange}
                      className="mt-2 h-12 w-full rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-semibold text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                    >
                      <option value="false">Ні</option>
                      <option value="true">Так</option>
                    </select>
                  </label>

                  <label className="block">
                    <span className="text-sm font-black uppercase text-black">
                      Ким працюєте зараз
                    </span>
                    <input
                      name="currentJob"
                      value={applicationForm.currentJob}
                      onChange={handleApplicationChange}
                      className="mt-2 h-12 w-full rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-semibold text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                    />
                  </label>

                  <label className="block">
                    <span className="text-sm font-black uppercase text-black">
                      Ким працювали раніше
                    </span>
                    <input
                      name="previousJob"
                      value={applicationForm.previousJob}
                      onChange={handleApplicationChange}
                      className="mt-2 h-12 w-full rounded-2xl border-2 border-black/10 bg-white px-4 text-sm font-semibold text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                    />
                  </label>
                </div>

                <label className="block">
                  <span className="text-sm font-black uppercase text-black">
                    Чому хочете потрапити в МОЖУ?
                  </span>
                  <textarea
                    rows={4}
                    name="motivation"
                    value={applicationForm.motivation}
                    onChange={handleApplicationChange}
                    className="mt-2 w-full rounded-2xl border-2 border-black/10 bg-white px-4 py-3 text-sm font-semibold leading-6 text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-black uppercase text-black">
                    Що хочете змінити у своєму житті за 3 місяці?
                  </span>
                  <textarea
                    rows={4}
                    name="threeMonthGoal"
                    value={applicationForm.threeMonthGoal}
                    onChange={handleApplicationChange}
                    className="mt-2 w-full rounded-2xl border-2 border-black/10 bg-white px-4 py-3 text-sm font-semibold leading-6 text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                  />
                </label>

                <label className="block">
                  <span className="text-sm font-black uppercase text-black">
                    Яку користь можете дати спільноті?
                  </span>
                  <textarea
                    rows={4}
                    name="communityContribution"
                    value={applicationForm.communityContribution}
                    onChange={handleApplicationChange}
                    className="mt-2 w-full rounded-2xl border-2 border-black/10 bg-white px-4 py-3 text-sm font-semibold leading-6 text-black outline-none transition focus:border-black focus:ring-4 focus:ring-[#ffd400]/30"
                  />
                </label>
              </div>

              <div className="flex justify-end border-t border-[#EEEAE4] bg-[#FCFBF9] px-5 py-4 sm:px-7">
                <button
                  type="submit"
                  disabled={applicationLoading}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000] transition hover:-translate-y-0.5 disabled:cursor-wait disabled:opacity-60"
                >
                  <Save size={17} />
                  {applicationLoading ? "Зберігаємо..." : "Зберегти анкету"}
                </button>
              </div>
            </form>

            <div className="rounded-lg border border-[#E3DED6] bg-white">
              <div className="border-b border-[#EEEAE4] px-5 py-5 sm:px-7">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-lg bg-[#FFF7C8] text-black">
                    <FileText size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#312D28]">
                      Резюме
                    </h3>
                    <p className="text-sm text-[#857E76]">
                      Завантажте актуальний файл, щоб команда могла швидше
                      допомогти з вакансіями та консультаціями.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-5 px-5 py-6 sm:px-7">
                <ProgramMaterials
                  materials={resumeMaterial}
                  title="Поточне резюме"
                  emptyText="Резюме ще не завантажено."
                />

                <div className="rounded-3xl border border-[#E8E1D8] bg-[#fffdf7] p-5">
                  <div className="mb-4">
                    <p className="text-sm font-black text-black">
                      Завантажити або замінити резюме
                    </p>
                    <p className="mt-1 text-xs font-semibold text-[#817A72]">
                      Підтримуються PDF, DOC, DOCX, TXT. Максимальний розмір -
                      10 MB.
                    </p>
                  </div>

                  <MaterialDropzone
                    files={resumeFiles}
                    onFilesChange={setResumeFiles}
                    maxFiles={1}
                    accept=".pdf,.doc,.docx,.txt"
                  />

                  <div className="mt-4 flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <p className="text-xs leading-5 text-[#857E76]">
                      Новий файл замінить попереднє резюме у профілі.
                    </p>
                    <button
                      type="button"
                      onClick={handleResumeUpload}
                      disabled={resumeLoading || resumeFiles.length === 0}
                      className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl border-2 border-black bg-[#ffd400] px-5 text-sm font-black text-black shadow-[4px_4px_0_#000] transition hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <Save size={17} />
                      {resumeLoading ? "Завантажуємо..." : "Завантажити резюме"}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <form
              onSubmit={handlePasswordSubmit}
              className="rounded-lg border border-[#E3DED6] bg-white"
            >
              <div className="flex flex-col gap-4 border-b border-[#EEEAE4] px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-7">
                <div className="flex items-center gap-3">
                  <div className="grid h-10 w-10 place-items-center rounded-lg bg-[#F1EEF9] text-[#6850A5]">
                    <LockKeyhole size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#312D28]">
                      Пароль і безпека
                    </h3>
                    <p className="text-sm text-[#857E76]">
                      Регулярно оновлюйте пароль для захисту акаунта.
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => setShowPasswords((current) => !current)}
                  className="inline-flex h-9 items-center justify-center gap-2 rounded-lg border border-[#DDD7CF] px-3 text-sm font-medium text-[#625C55] hover:bg-[#F7F4EF]"
                >
                  {showPasswords ? <EyeOff size={16} /> : <Eye size={16} />}
                  {showPasswords ? "Приховати" : "Показати паролі"}
                </button>
              </div>

              <div className="space-y-5 px-5 py-6 sm:px-7">
                <label className="block">
                  <span className="mb-2 flex items-center gap-2 text-sm font-semibold text-[#4E4943]">
                    <KeyRound size={16} className="text-[#725DA8]" />
                    Поточний пароль
                  </span>
                  <input
                    type={showPasswords ? "text" : "password"}
                    name="oldPassword"
                    autoComplete="current-password"
                    className="h-12 w-full rounded-lg border border-[#DCD7CF] bg-[#FCFBF9] px-4 text-sm text-[#39342F] outline-none transition focus:border-[#725DA8] focus:bg-white focus:ring-2 focus:ring-[#725DA8]/15"
                    value={passwordData.oldPassword}
                    onChange={handlePasswordChange}
                  />
                </label>

                <div className="grid gap-5 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-[#4E4943]">
                      Новий пароль
                    </span>
                    <input
                      type={showPasswords ? "text" : "password"}
                      name="newPassword"
                      autoComplete="new-password"
                      className="h-12 w-full rounded-lg border border-[#DCD7CF] bg-[#FCFBF9] px-4 text-sm text-[#39342F] outline-none transition focus:border-[#725DA8] focus:bg-white focus:ring-2 focus:ring-[#725DA8]/15"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                    />
                  </label>

                  <label className="block">
                    <span className="mb-2 block text-sm font-semibold text-[#4E4943]">
                      Повторіть новий пароль
                    </span>
                    <input
                      type={showPasswords ? "text" : "password"}
                      name="confirmPassword"
                      autoComplete="new-password"
                      className="h-12 w-full rounded-lg border border-[#DCD7CF] bg-[#FCFBF9] px-4 text-sm text-[#39342F] outline-none transition focus:border-[#725DA8] focus:bg-white focus:ring-2 focus:ring-[#725DA8]/15"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                    />
                  </label>
                </div>

                <p className="text-xs leading-5 text-[#857E76]">
                  Використайте щонайменше 8 символів та одну велику літеру.
                </p>
              </div>

              <div className="flex justify-end border-t border-[#EEEAE4] bg-[#FCFBF9] px-5 py-4 sm:px-7">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-[#625096] px-5 text-sm font-semibold text-white transition-colors hover:bg-[#524281] disabled:cursor-wait disabled:opacity-60"
                >
                  <LockKeyhole size={17} />
                  {passwordLoading ? "Змінюємо пароль..." : "Оновити пароль"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Settings;
